// All engines ultimately funnel through the single shared cTrader stream
// (shared-ctrader-stream), so "gateway connected" is derived from how many
// account streams report 'connected'.

import { getAllChinEngines } from './chin-bot-engine';
import { getAllAutoChinEngines } from './auto-chin-bot-engine';
import { getAllTrailingEngines } from './trailing-stop-engine';
import { isCopyTradeEngineRunning } from './copy-trade-engine';
import { isOrderAlertWatching } from './ctrader-order-alert';
import { getAllStreamStatuses } from './shared-ctrader-stream';

export interface EngineStatus {
  chinRunning: number;
  autoChinRunning: number;
  trailingRunning: number;
  copyRunning: boolean;
  orderAlertWatching: boolean;
  gatewayConnected: boolean;
  streamsConnected: number;
  streamsTotal: number;
  anyBotRunning: boolean;
  totalBotsRunning: number;
  /** Timestamp (ms) when the bots first started this run, or null if idle. */
  startedAt: number | null;
  /** Milliseconds the bots have been continuously running. */
  uptimeMs: number;
  lastUpdated: number;
}

// Module-level marker of when the current "running" stretch began.
// Set on the first poll where any bot is live, cleared when all stop.
let engineStartedAt: number | null = null;

/**
 * Build a fresh snapshot of the current engine + gateway state.
 * Cheap and synchronous — safe to call on a short polling interval.
 */
export function getEngineStatus(): EngineStatus {
  const chinRunning = [...getAllChinEngines().values()].filter((e) => e.isRunning).length;
  const autoChinRunning = [...getAllAutoChinEngines().values()].filter((e) => e.isRunning).length;
  const trailingRunning = getAllTrailingEngines().filter((e) => e.isRunning).length;
  const copyRunning = isCopyTradeEngineRunning();
  const orderAlertWatching = isOrderAlertWatching();

  const streams = getAllStreamStatuses();
  const streamsConnected = streams.filter((s) => s.status === 'connected').length;
  const streamsTotal = streams.length;

  const anyBotRunning = chinRunning > 0 || autoChinRunning > 0 || trailingRunning > 0 || copyRunning;
  const totalBotsRunning = chinRunning + autoChinRunning + trailingRunning + (copyRunning ? 1 : 0);

  // ─── Uptime tracking ─────────────────────────────────────
  if (anyBotRunning && engineStartedAt === null) {
    engineStartedAt = Date.now();
  } else if (!anyBotRunning) {
    engineStartedAt = null;
  }
  const uptimeMs = engineStartedAt !== null ? Date.now() - engineStartedAt : 0;

  return {
    chinRunning,
    autoChinRunning,
    trailingRunning,
    copyRunning,
    orderAlertWatching,
    gatewayConnected: streamsConnected > 0,
    streamsConnected,
    streamsTotal,
    anyBotRunning,
    totalBotsRunning,
    startedAt: engineStartedAt,
    uptimeMs,
    lastUpdated: Date.now(),
  };
}